import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coreServicesApi, type RoastProfile } from '../lib/api/core-services.js';

// Query keys for Hotel Bull data
export const hotelBullKeys = {
  all: ['hotelBull'] as const,
  health: () => [...hotelBullKeys.all, 'health'] as const,
  stats: (guildId?: string) => [...hotelBullKeys.all, 'stats', guildId] as const,
  activityHistory: (guildId?: string, days?: number) => [...hotelBullKeys.all, 'activity', guildId, days] as const,
  profiles: () => [...hotelBullKeys.all, 'profiles'] as const,
  profile: (hashedUserId: string, guildId: string) => [...hotelBullKeys.profiles(), hashedUserId, guildId] as const,
  recentProfiles: (limit: number) => [...hotelBullKeys.profiles(), 'recent', limit] as const,
  settings: () => [...hotelBullKeys.all, 'settings'] as const,
  guildSettings: (guildId: string) => [...hotelBullKeys.settings(), guildId] as const,
  target: (hashedUserId: string, guildId: string) => [...hotelBullKeys.all, 'target', hashedUserId, guildId] as const,
};

// Health check hook
export const useHotelBullHealth = () => {
  return useQuery({
    queryKey: hotelBullKeys.health(),
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.health();
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
  });
};

// Stats hook
export const useHotelBullStats = (guildId?: string) => {
  return useQuery({
    queryKey: hotelBullKeys.stats(guildId),
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.getStats(guildId);
      return response;
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  });
};

// Activity history hook
export const useHotelBullActivityHistory = (guildId?: string, days: number = 7) => {
  return useQuery({
    queryKey: hotelBullKeys.activityHistory(guildId, days),
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.getActivityHistory(guildId, days);
      return response;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
};

// Recent profiles hook
export const useRecentProfiles = (limit: number = 10) => {
  return useQuery({
    queryKey: hotelBullKeys.recentProfiles(limit),
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.getRecentProfiles(limit);
      return response.profiles; // Extract profiles array from response
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// LLM stats hook - provider performance metrics
export const useHotelBullLlmStats = (hours: number = 24) => {
  return useQuery({
    queryKey: [...hotelBullKeys.all, 'llmStats', hours] as const,
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.getLlmStats(hours);
      return response;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });
};

// Bot health hook - actual bot process health (Discord connection, uptime, etc.)
export const useHotelBullBotHealth = () => {
  return useQuery({
    queryKey: [...hotelBullKeys.all, 'botHealth'] as const,
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.getBotHealth();
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
  });
};

// Individual profile hook
export const useRoastProfile = (hashedUserId: string, guildId: string, enabled = true) => {
  return useQuery({
    queryKey: hotelBullKeys.profile(hashedUserId, guildId),
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.getProfile(hashedUserId, guildId);
      return response;
    },
    enabled: enabled && !!hashedUserId && !!guildId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Guild settings hook
export const useGuildSettings = (guildId: string, enabled = true) => {
  return useQuery({
    queryKey: hotelBullKeys.guildSettings(guildId),
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.getSettings(guildId);
      return response;
    },
    enabled: enabled && !!guildId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Target settings hook
export const useTargetSettings = (hashedUserId: string, guildId: string, enabled = true) => {
  return useQuery({
    queryKey: hotelBullKeys.target(hashedUserId, guildId),
    queryFn: async () => {
      const response = await coreServicesApi.hotelBull.getTargetSettings(hashedUserId, guildId);
      return response;
    },
    enabled: enabled && !!hashedUserId && !!guildId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Profile operations hook
export const useProfileOperations = () => {
  const queryClient = useQueryClient();

  const toggleProfile = useMutation({
    mutationFn: ({ hashedUserId, guildId, enabled }: { hashedUserId: string; guildId: string; enabled: boolean }) =>
      coreServicesApi.hotelBull.toggleProfile(hashedUserId, guildId, enabled),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.profile(variables.hashedUserId, variables.guildId) });
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.recentProfiles(10) });
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.stats() });
    },
  });

  const deleteProfile = useMutation({
    mutationFn: ({ hashedUserId, guildId }: { hashedUserId: string; guildId: string }) =>
      coreServicesApi.hotelBull.deleteProfile(hashedUserId, guildId),
    onSuccess: (_, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.profile(variables.hashedUserId, variables.guildId) });
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.recentProfiles(10) });
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.stats() });
    },
  });

  const updateProfile = useMutation({
    mutationFn: (profile: Partial<RoastProfile>) =>
      coreServicesApi.hotelBull.updateProfile(profile),
    onSuccess: () => {
      // Invalidate all profile-related queries
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.profiles() });
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.stats() });
    },
  });

  const updateChannelExclusion = useMutation({
    mutationFn: ({ hashedUserId, guildId, channelId, exclude }: { hashedUserId: string; guildId: string; channelId: string; exclude: boolean }) =>
      coreServicesApi.hotelBull.updateChannelExclusion(hashedUserId, guildId, channelId, exclude),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.profile(variables.hashedUserId, variables.guildId) });
    },
  });

  return {
    toggleProfile,
    deleteProfile,
    updateProfile,
    updateChannelExclusion,
    isLoading: toggleProfile.isPending || deleteProfile.isPending || updateProfile.isPending || updateChannelExclusion.isPending,
    error: toggleProfile.error || deleteProfile.error || updateProfile.error || updateChannelExclusion.error,
  };
};

// Target operations hook
export const useTargetOperations = () => {
  const queryClient = useQueryClient();

  const setTarget = useMutation({
    mutationFn: ({ hashedUserId, guildId, enabled }: { hashedUserId: string; guildId: string; enabled: boolean }) =>
      coreServicesApi.hotelBull.setTarget(hashedUserId, guildId, enabled),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.target(variables.hashedUserId, variables.guildId) });
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.stats() });
    },
  });

  const updateTargetStats = useMutation({
    mutationFn: ({ hashedUserId, guildId }: { hashedUserId: string; guildId: string }) =>
      coreServicesApi.hotelBull.updateTargetStats(hashedUserId, guildId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.target(variables.hashedUserId, variables.guildId) });
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.stats() });
    },
  });

  return {
    setTarget,
    updateTargetStats,
    isLoading: setTarget.isPending || updateTargetStats.isPending,
    error: setTarget.error || updateTargetStats.error,
  };
};

// Server settings operations hook
export const useServerSettingsOperations = () => {
  const queryClient = useQueryClient();

  const setReplyChannel = useMutation({
    mutationFn: ({ guildId, channelId }: { guildId: string; channelId: string }) =>
      coreServicesApi.hotelBull.setReplyChannel(guildId, channelId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.guildSettings(variables.guildId) });
    },
  });

  const setTargetOnlyMode = useMutation({
    mutationFn: ({ guildId, enabled }: { guildId: string; enabled: boolean }) =>
      coreServicesApi.hotelBull.setTargetOnlyMode(guildId, enabled),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.guildSettings(variables.guildId) });
    },
  });

  return {
    setReplyChannel,
    setTargetOnlyMode,
    isLoading: setReplyChannel.isPending || setTargetOnlyMode.isPending,
    error: setReplyChannel.error || setTargetOnlyMode.error,
  };
};

// Roast session operations hook
export const useRoastSessionOperations = () => {
  const queryClient = useQueryClient();

  const updateComeback = useMutation({
    mutationFn: ({ sessionId, userReply, botComeback }: { sessionId: string; userReply: string; botComeback: string }) =>
      coreServicesApi.hotelBull.updateRoastSessionComeback(sessionId, userReply, botComeback),
    onSuccess: () => {
      // Invalidate stats as they might change with new session data
      queryClient.invalidateQueries({ queryKey: hotelBullKeys.stats() });
    },
  });

  return {
    updateComeback,
    isLoading: updateComeback.isPending,
    error: updateComeback.error,
  };
};